import React, { useState } from "react"
import { graphql, Link } from "gatsby"
import ReactTooltip from "react-tooltip"

import Layout from "../components/common/layout"
import SEO from "../components/common/seo"
import TopicList from "../components/topic/topiclist"
import CommunityFeatures from "../components/feature/communityfeatures"
import GummySearchAnalysis from "../components/community/gummysearchAnalysis"
import SimilarCommunities from "../components/community/similarcommunities"
import CommunityReviews from "../components/community/reviews"
import CommunityStarRating from "../components/community/starrating"
import CommunityMonthlyStats from "../components/community/communityMonthlyStats"
import CommunityFaq from "../components/community/communityFaq"
import PaidAds from "../components/promotions/paidAds"
import StickyScrollWrapper from "../components/common/stickyScrollWrapper"
import ShareButtons from "../components/common/sharebuttons"
import CommunityVerifiedBadge from "../components/common/communityVerifiedBadge"
// import Adsense from "../components/promotions/adsense"
import Deals from "../components/promotions/deals"

import { ModalRoutingContext } from "gatsby-plugin-modal-routing-3"

import {
  readableNumber,
  capitalizeFirst,
  unslugify,
  ordinalWord,
} from "../utils/text"
import { average } from "../utils/numbers"
import { setLogoImgFallbackOnError } from "../utils/logoPlaceholder"
import { trackEvent } from "../utils/tracking"
import { buildGummysearchGlanceParagraph } from "../utils/gummysearchSummary"
import {
  getCommunityRankOnTopicPageDefault,
  getCommunityRankOnTopicPageLargest,
} from "../utils/communities"
import { topicIsoDate, topicSeoDate } from "../utils/topics"
import { getSchemaForCommunityListing } from "../utils/schema"

// clean these up below
import {
  UserGroupIcon,
  FlagIcon,
  ExternalLinkIcon,
} from "@heroicons/react/outline"

// Same outbound query params as src/pages/redirect.jsx applies before leaving the site.
function outboundCommunityUrl(rawUrl, communityStatus) {
  if (!rawUrl) return null
  let out = rawUrl
  if (!out.includes("#")) {
    if (out.includes("?")) {
      out += "&ref=thehiveindex.com&utm_source=thehiveindex.com"
    } else {
      out += "?ref=thehiveindex.com&utm_source=thehiveindex.com"
    }
    if (communityStatus === "sponsored") {
      out += "&utm_campaign=featured"
    }
  }
  return out
}

const REDIRECT_DISABLED_TOOLTIP = "It's no longer on an active platform."

const NAS_IO_PLATFORM_FAQ_ANSWER =
  "Nas.io is the platform for creator communities. It integrates with existing chat community software like WhatsApp, Discord, and Slack, and brings together the community members in a unified fashion."

const CommunityPage = ({ data, classes, pageContext }) => {
  const community = data.community
  const platformNodes = data.communityPlatforms.nodes
  const platformSlugsFromCommunity = (community.platform || "")
    .split(",")
    .map(s => s.trim())
    .filter(Boolean)
  let platform = null
  for (const slug of platformSlugsFromCommunity.length
    ? platformSlugsFromCommunity
    : ["independent"]) {
    const n = platformNodes.find(x => x.slug === slug)
    if (n) {
      platform = n
      break
    }
  }
  const redirectDisabled =
    Boolean(community.url) &&
    platformSlugsFromCommunity.length > 0 &&
    platformSlugsFromCommunity.every(slug => {
      const n = platformNodes.find(x => x.slug === slug)
      return n != null && n.removed === true
    })
  const communityOnRemovedPlatform = platformSlugsFromCommunity.some(slug => {
    const n = platformNodes.find(x => x.slug === slug)
    return n != null && n.removed === true
  })
  const inactivePlatformTitleAddon = communityOnRemovedPlatform ? (
    <span className="inline-flex shrink-0 items-center rounded-md bg-red-600 px-2.5 py-0.5 text-xs font-semibold text-white">
      Inactive
    </span>
  ) : null
  const topics = data.topics.nodes
  const topicRanksBySlug = {}
  topics.forEach(t => {
    const inTopic = data.topicRankingCommunities.nodes.filter(
      c => c.topics && c.topics.split(",").includes(t.slug)
    )
    const rank = getCommunityRankOnTopicPageDefault(
      community.slug,
      t.slug,
      inTopic
    )
    if (rank != null && rank <= 10) {
      topicRanksBySlug[t.slug] = rank
    }
  })
  const similarCommunities = data.similarCommunities.nodes
  const reviews = data.reviews.nodes
  const gummysearch = data.gummysearchSubreddit?.reddit_subreddit?.[0] || null
  const gummysearchGlanceParagraph = buildGummysearchGlanceParagraph(
    gummysearch
  )
  const displayMembers = community.members
  const [showAllSimilarCommunities, setShowAllSimilarCommunities] = useState(
    false
  )

  const listingDatesSummary = (() => {
    const addedIso = topicIsoDate(community.added)
    const updatedIso = topicIsoDate(community.updated)
    const hasAdded = Boolean(addedIso)
    const hasUpdated = Boolean(updatedIso)
    if (hasAdded && hasUpdated) {
      if (addedIso === updatedIso) {
        return `Added ${topicSeoDate(community.added)}`
      }
      return `Added ${topicSeoDate(community.added)} · Updated ${topicSeoDate(
        community.updated
      )}`
    }
    if (hasAdded) return `Added ${topicSeoDate(community.added)}`
    if (hasUpdated) return `Added ${topicSeoDate(community.updated)}`
    return null
  })()

  // keeping this here for now b/c we track certain parameters about the current url
  const clickedLink = () => {
    if (typeof window.gtag !== "undefined") {
      window.gtag("event", "Redirect", {
        event_label: community.name,
      })
    }
  }

  // make the description
  var generatedDescription = `${community.name} is`
  if (platform && platform.slug !== "independent") {
    generatedDescription += ` a ${platform.groupName}`
  } else {
    generatedDescription += ` an online community`
  }
  if (topics.length) {
    generatedDescription += ` for `
    var communityTopics = topics.map((t, i) => (t.people ? t.people : t.name))
    if (communityTopics.length > 1) {
      communityTopics[communityTopics.length - 1] = `and ${
        communityTopics[communityTopics.length - 1]
      }`
    }
    if (communityTopics.length === 2) {
      generatedDescription += communityTopics.join(" ")
    } else {
      generatedDescription += communityTopics.join(", ")
    }
  }
  if (community.members) {
    generatedDescription += ` with roughly ${readableNumber(
      displayMembers
    )} members`
  }
  generatedDescription += `.`

  const yearEstablished = parseInt(community.established, 10)
  const maxYear = new Date().getFullYear() + 1
  if (
    community.established != null &&
    community.established !== "" &&
    !Number.isNaN(yearEstablished) &&
    yearEstablished >= 1900 &&
    yearEstablished <= maxYear
  ) {
    generatedDescription += ` It has been around since ${yearEstablished}.`
  }

  // add communication style
  if (community.forum && community.chat) {
    generatedDescription +=
      " It has both a forum and a live chat communication style."
  } else if (community.forum) {
    generatedDescription += " It uses a forum format for communication."
  } else if (community.chat) {
    generatedDescription += " It has a live chat communication style."
  }

  const otherFeatureLabels = {
    apply: "an application process to join",
    pairing: "1:1 member pairing",
    events: "events or meetups",
    perks: "member perks or discounts",
    jobs: "job listings or career networking",
    newsletter: "an email newsletter",
    paid: "paid membership options",
    courses: "courses or structured learning",
    tools: "curated tools and resources",
    token: "a community token or on-chain element",
  }
  const activeOtherFeatures = Object.keys(otherFeatureLabels).filter(
    key => community[key]
  )
  if (activeOtherFeatures.length) {
    const labels = activeOtherFeatures.map(key => otherFeatureLabels[key])
    let featureList
    if (labels.length === 1) {
      featureList = labels[0]
    } else if (labels.length === 2) {
      featureList = `${labels[0]} and ${labels[1]}`
    } else {
      featureList = `${labels.slice(0, -1).join(", ")}, and ${
        labels[labels.length - 1]
      }`
    }
    generatedDescription += ` It also includes ${featureList}.`
  }

  const topicRankParts = Object.entries(topicRanksBySlug)
    .map(([topicSlug, rank]) => {
      const t = topics.find(x => x.slug === topicSlug)
      const topicName =
        t && t.name && String(t.name).trim()
          ? String(t.name).trim()
          : capitalizeFirst(unslugify(topicSlug))
      return { rank, listPhrase: `the ${topicName} communities list` }
    })
    .sort((a, b) => a.rank - b.rank || a.listPhrase.localeCompare(b.listPhrase))
  if (topicRankParts.length) {
    const segments = topicRankParts.map(x => `#${x.rank} in ${x.listPhrase}`)
    let rankClause
    if (segments.length === 1) {
      rankClause = segments[0]
    } else if (segments.length === 2) {
      rankClause = `${segments[0]} and ${segments[1]}`
    } else {
      rankClause = `${segments.slice(0, -1).join(", ")}, and ${
        segments[segments.length - 1]
      }`
    }
    generatedDescription += ` On the Hive Index it ranks ${rankClause}.`
  }

  const topicSizeRankParts = []
  topics.forEach(t => {
    const inTopic = data.topicRankingCommunities.nodes.filter(
      c => c.topics && c.topics.split(",").includes(t.slug)
    )
    const rankPopular = getCommunityRankOnTopicPageDefault(
      community.slug,
      t.slug,
      inTopic
    )
    const rankLargest = getCommunityRankOnTopicPageLargest(
      community.slug,
      t.slug,
      inTopic
    )
    const topicName =
      t && t.name && String(t.name).trim()
        ? String(t.name).trim()
        : capitalizeFirst(unslugify(t.slug))
    if (
      (rankPopular == null || rankPopular > 10) &&
      rankLargest != null &&
      rankLargest <= 10
    ) {
      topicSizeRankParts.push({ rank: rankLargest, topicName })
    }
  })
  topicSizeRankParts.sort(
    (a, b) => a.rank - b.rank || a.topicName.localeCompare(b.topicName)
  )
  if (topicSizeRankParts.length) {
    const segments = topicSizeRankParts.map(
      x =>
        `the ${ordinalWord(x.rank)} largest community in the ${
          x.topicName
        } topic page`
    )
    let sizeClause
    if (segments.length === 1) {
      sizeClause = segments[0]
    } else if (segments.length === 2) {
      sizeClause = `${segments[0]} and ${segments[1]}`
    } else {
      sizeClause = `${segments.slice(0, -1).join(", ")}, and ${
        segments[segments.length - 1]
      }`
    }
    generatedDescription += ` It is ${sizeClause}.`
  }

  const reviewAvgDecimal =
    reviews.length > 0
      ? Math.min(5, Math.max(0, average(reviews.map(r => r.overall)))).toFixed(
          1
        )
      : null
  const communityForRating = reviewAvgDecimal
    ? {
        ...community,
        reviewAvg: reviewAvgDecimal,
        reviewCount: String(reviews.length),
      }
    : community

  // if (reviewAvgDecimal) {
  //   const reviewWord = reviews.length === 1 ? "review" : "reviews"
  //   generatedDescription = `${reviewAvgDecimal} star rating from ${reviews.length} ${reviewWord}. ${generatedDescription}`
  // }

  // url we redirect to is the community url. construct the internal one too for tracking
  const linkUrl = `${community.url}`
  const status = community.sponsored
    ? "sponsored"
    : community.promoted
    ? "promoted"
    : community.gold
    ? "gold"
    : community.affiliate
    ? "affiliate"
    : "free"
  var internalLinkUrl = `/redirect/?url=${encodeURIComponent(
    linkUrl
  )}&name=${encodeURIComponent(community.name)}&slug=${community.slug}&topics=${
    community.topics
  }&platform=${community.platform}&status=${status}`
  const redirectLinkState = { modal: true }
  const directOutboundUrl = outboundCommunityUrl(linkUrl, status)

  const clickedDirectOutboundLink = () => {
    clickedLink()
    trackEvent("Redirected", {
      event_label: community.name,
      name: community.name,
      slug: community.slug,
      platform: community.platform,
      topics: community.topics,
      redirect_style: "direct",
      status,
    })
  }

  // this should be just the host really
  const linkDisplay = linkUrl
    .replace("www.", "")
    .replace(`https://`, "")
    .replace(`http://`, "")
    .split("?")[0]

  const reviewSnippet = reviewAvgDecimal
    ? `${reviewAvgDecimal} stars (${reviews.length} reviews). `
    : ""

  // open graph image
  const logoUrl = community.logo || (platform && platform.logoBig)
  const metaSubtitle = `${
    platform && platform.slug !== "independent"
      ? `${platform.groupName}`
      : "Online Community"
  } for ${community.topics
    .split(",")
    .map(t => capitalizeFirst(t))
    .join(", ")}`
  const seoImageUrl = `https://og.tailgraph.com/og?fontFamily=Open%2BSans&title=${encodeURIComponent(
    community.name
  )}&titleTailwind=font-bold%20text-6xl%20text-rose-600&text=${encodeURIComponent(
    metaSubtitle
  )}&textTailwind=text-2xl%20mt-4%20text-yellow-600&textFontFamily=Source%2BSans%2BPro&logoUrl=${logoUrl}&logoTailwind=mx-auto%20h-48%20mb-4&bgUrl=https%3A%2F%2Fthehiveindex.com%2Fstatic%2F6f9a721ed54860c63211350929d004b7%2F9c94d%2Ficon.png&bgTailwind=bg-white&overlay=1&overlayTailwind=bg-white%20opacity-95&footer=thehiveindex.com&footerTailwind=font-bold%20text-rose-500%20text-center%20text-xl&containerTailwind=bg-transparent`

  // Featured / Hive Gold: hide deals strip and similar-community top picks / ratings (paid-ad slot always shows).
  const hideAds = community.promoted || community.gold

  // get the deals (nodes are all current deals, this filters down to unique ones that overlap on topics)
  var uniqueDeals = []
  data.deals.nodes
    .filter(d => community.topics.split(",").includes(d.topic))
    .forEach(d => {
      if (!uniqueDeals.find(d2 => d2.name === d.name)) {
        uniqueDeals.push(d)
      }
    })

  // dont index if the 2 combined community description fields length is < 350
  const combinedDescriptionLength = (
    generatedDescription + (community.description ? community.description : "")
  ).length
  // const dontIndex = combinedDescriptionLength < 150; // really short ones dont index
  const dontIndex = false // dont do this exception anymore. we dont index at sitemap level if views is 0.

  const communityNameLower = String(community.name || "").toLowerCase()
  /** Only used in the "What topics … cover?" FAQ so we don't sprinkle links across long answers. */
  const faqTopicAnswerLinkTargets = topics
    .map(t => ({
      label: String((t.people ? t.people : t.name) || "").trim(),
      href: `/topics/${t.slug}/`,
    }))
    .filter(x => x.label.length > 0)
    .filter(x => {
      if (x.label.length < 2) return true
      return !communityNameLower.includes(x.label.toLowerCase())
    })

  const faqItems = []

  const topicPeopleLabels = topics.map(t => (t.people ? t.people : t.name))
  const whoTopic = topicPeopleLabels.length
    ? topicPeopleLabels.join(" and ")
    : "people building communities"
  const whoFeatureBits = []
  if (community.forum) whoFeatureBits.push("forum discussion")
  if (community.chat) whoFeatureBits.push("live chat")
  if (community.events) whoFeatureBits.push("events or meetups")
  const whoFeatureStr = whoFeatureBits.length
    ? whoFeatureBits.join(", ")
    : "conversation with other members"
  const whoPlatform =
    platform && platform.slug !== "independent"
      ? `${platform.name}`
      : "an independent setup"
  faqItems.push({
    question: `Who is ${community.name} for?`,
    answerPlain: `Best for ${whoTopic} enthusiasts looking for a ${whoPlatform}-based community with ${whoFeatureStr}.`,
  })

  if (community.paid) {
    faqItems.push({
      question: `Is ${community.name} free to join?`,
      answerPlain: `${community.name} is listed with paid membership options. The community link spells out current pricing and member benefits.`,
    })
  } else {
    faqItems.push({
      question: `Is ${community.name} free to join?`,
      answerPlain: `This listing is not marked as paid-only. Access rules and any fees are decided by the community.`,
    })
  }
  if (community.members) {
    faqItems.push({
      question: `How many members does ${community.name} have?`,
      answerPlain: `Roughly ${readableNumber(
        displayMembers
      )} members, based on figures reported by the community or its host. Member counts are approximate and change over time.`,
    })
  }
  if (platform && platform.slug !== "independent") {
    let platAnswer
    if (platform.slug === "nas-io") {
      platAnswer = NAS_IO_PLATFORM_FAQ_ANSWER
    } else if (platform.description && String(platform.description).trim()) {
      platAnswer = String(platform.description).trim()
    } else {
      platAnswer = `${platform.name} hosts this community. The ${platform.name} platform page on the Hive Index lists comparable communities on the same stack.`
    }
    const platformLead = `${community.name} runs on ${platform.name}.`
    faqItems.push({
      question: `What platform is ${community.name} on?`,
      answerPlain: `${platformLead} ${platAnswer}`,
      answerElement: (
        <>
          {community.name} runs on{" "}
          <Link
            to={`/platforms/${platform.slug}/`}
            className="text-rose-600 font-medium underline underline-offset-2 hover:text-rose-700"
          >
            {platform.name}
          </Link>
          . {platAnswer}
        </>
      ),
    })
  }
  if (topics.length) {
    const topicLabels = topics
      .map(t => (t.people ? t.people : t.name))
      .join(", ")
    faqItems.push({
      question: `What topics does ${community.name} cover?`,
      answerPlain: `On the Hive Index, ${community.name} is organized under ${topicLabels}.`,
      ...(faqTopicAnswerLinkTargets.length
        ? { answerLinkTargets: faqTopicAnswerLinkTargets }
        : {}),
    })
  }
  if (community.url && !redirectDisabled) {
    faqItems.push({
      question: `How do I join ${community.name}?`,
      answerPlain: `You can join ${community.name} by clicking the link in this answer, or pressing the "Go to community" button above.`,
      answerElement: (
        <>
          You can join {community.name} by clicking{" "}
          <Link
            to={internalLinkUrl}
            state={redirectLinkState}
            onClick={clickedLink}
            rel="nofollow"
            className="text-rose-600 font-medium underline underline-offset-2 hover:text-rose-700"
          >
            this link
          </Link>
          {', or pressing the "Go to community" button above.'}
        </>
      ),
    })
  }
  /* One topic blurb FAQ avoids near-duplicate paragraphs when a community spans overlapping categories (e.g. Web Development + Software Development). */
  const primaryTopicWithDescription = topics.find(
    t => t.description && String(t.description).trim().length >= 20
  )
  if (primaryTopicWithDescription) {
    const d = String(primaryTopicWithDescription.description).trim()
    const topicTitle = primaryTopicWithDescription.name
      ? String(primaryTopicWithDescription.name).trim()
      : capitalizeFirst(unslugify(primaryTopicWithDescription.slug))
    faqItems.push({
      question: `What are the ${topicTitle} communities like?`,
      answerPlain: d,
    })
  }

  const pageUrl = `https://thehiveindex.com/communities/${community.slug}/`
  const logoForSchema = community.logo || (platform && platform.logoBig)
  const primaryTopic = topics[0]
  const breadcrumbItems = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: "https://thehiveindex.com/",
    },
  ]
  if (primaryTopic) {
    breadcrumbItems.push({
      "@type": "ListItem",
      position: 2,
      name: primaryTopic.name || capitalizeFirst(unslugify(primaryTopic.slug)),
      item: `https://thehiveindex.com/topics/${primaryTopic.slug}/`,
    })
  }
  breadcrumbItems.push({
    "@type": "ListItem",
    position: breadcrumbItems.length + 1,
    name: community.name,
    item: pageUrl,
  })

  const seoPlainForSchema = `${reviewSnippet}${generatedDescription} ${
    community.description ? community.description : ""
  }`.trim()
  const generatedDescriptionPlusListing = `${generatedDescription}${
    community.description ? ` ${community.description}` : ""
  }`

  const structuredData = getSchemaForCommunityListing({
    communitySlug: community.slug,
    communityName: community.name,
    shortDescriptionPlain: seoPlainForSchema,
    generatedDescriptionPlusSource: generatedDescriptionPlusListing,
    outboundUrlRaw: redirectDisabled ? "" : community.url || "",
    logoUrl: logoForSchema,
    reviews,
    breadcrumbListItems: breadcrumbItems,
  })

  const similarCommunitiesInitialVisible = 6

  // contents. either rendered in layout or a modal
  const communityContents = (
    <React.Fragment>
      <ReactTooltip
        id="community-redirect-disabled"
        place="top"
        effect="solid"
        backgroundColor="black"
        textColor="white"
      />
      <SEO
        title={`${community.name} - ${metaSubtitle}`}
        description={`${reviewSnippet}${generatedDescription} ${
          community.description ? community.description : ""
        }`}
        imageUrl={seoImageUrl}
        dontIndex={dontIndex}
        // imageSize={'small'}
        generateImage={false}
        path={`/communities/${community.slug}/`}
        schema={structuredData}
      />

      {/* Main 3 column grid */}
      <div
        className="grid grid-cols-1 gap-4 items-start lg:grid-cols-3 lg:gap-8"
        data-community-page-grid
      >
        {/* Left column */}
        <div className="grid grid-cols-1 gap-4 lg:col-span-2">
          {/* Welcome panel */}
          <section aria-labelledby="profile-overview-title">
            <div className="rounded-lg bg-white overflow-hidden shadow">
              <div className="sr-only" id="community-overview-title">
                Community Overview
              </div>
              <div className="bg-white p-6">
                <div className="sm:flex sm:items-center sm:justify-between flex-wrap">
                  <div className="sm:flex sm:space-x-5 mr-2 items-center">
                    <div className="flex-shrink-0">
                      {community.logo ? (
                        <img
                          className="mx-auto h-20 w-20 rounded-md object-contain"
                          src={community.logo}
                          alt={`${community.name} logo`}
                          onError={setLogoImgFallbackOnError}
                        />
                      ) : (
                        ""
                      )}
                    </div>
                    {/*<div className="text-center sm:mt-0 sm:text-left">
                      <h1 className="text-xl font-bold text-gray-900 sm:text-2xl mt-0">{community.name}</h1>
                    </div>*/}
                  </div>

                  {community.url ? (
                    <div className="mt-5 flex justify-center flex-shrink-0 sm:mt-0 ml-auto">
                      {redirectDisabled ? (
                        <span
                          data-for="community-redirect-disabled"
                          data-tip={REDIRECT_DISABLED_TOOLTIP}
                          className="inline-block cursor-not-allowed"
                        >
                          <div
                            aria-disabled="true"
                            className="flex items-center justify-between gap-3 px-4 py-2 border border-gray-300 text-gray-500 bg-gray-200 shadow-sm text-sm rounded-sm pointer-events-none"
                          >
                            <div className="min-w-0 flex-1 text-left">
                              <div className="font-medium max-w-xs truncate">
                                {linkDisplay}
                              </div>
                              <div className="opacity-75 text-xs font-semibold">
                                Go to community
                              </div>
                            </div>
                            <div className="flex-shrink-0">→</div>
                          </div>
                        </span>
                      ) : (
                        <Link
                          to={internalLinkUrl}
                          state={redirectLinkState}
                          onClick={clickedLink}
                          rel="nofollow"
                          className="flex items-center justify-between gap-3 px-4 py-2 border border-rose-600 text-white bg-rose-500 hover:bg-rose-600 shadow-sm text-sm rounded-sm text-white"
                        >
                          <div className="min-w-0 flex-1 text-left">
                            <div className="opacity-100 font-medium max-w-xs truncate">
                              {linkDisplay}
                            </div>
                            <div className="opacity-75 text-xs font-semibold">
                              Go to community
                            </div>
                          </div>
                          <div className="flex-shrink-0">→</div>
                        </Link>
                      )}
                    </div>
                  ) : (
                    ""
                  )}
                </div>

                {community.description && (
                  <div className="mt-4">
                    <h2
                      className="text-base font-medium text-gray-900 mb-1"
                      id="community-their-description"
                    >
                      About {community.name}
                    </h2>
                    <p
                      style={{ whiteSpace: "pre-wrap" }}
                      className="text-sm text-gray-500"
                    >
                      {community.description}
                    </p>
                  </div>
                )}
                <div className={community.description ? "mt-2" : "mt-4"}>
                  <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                    <h2
                      className="mb-0 text-base font-medium text-gray-900 mr-auto"
                      id="community-at-a-glance"
                    >
                      The community at a glance
                    </h2>
                    {reviews.length ? (
                      <CommunityStarRating
                        community={communityForRating}
                        starHeight={4}
                      />
                    ) : null}
                  </div>
                  <p className="text-sm text-gray-500">
                    {generatedDescription}
                  </p>
                  {gummysearchGlanceParagraph ? (
                    <p className="mt-3 text-sm text-gray-500">
                      {gummysearchGlanceParagraph}
                    </p>
                  ) : null}
                </div>
              </div>
              <div className="border-t border-gray-200 text-gray-600 bg-gray-50 flex flex-wrap divide-y divide-gray-200 sm:grid-cols-3 sm:divide-y-0 sm:divide-x">
                {platform && platform.slug !== "independent" ? (
                  <Link
                    to={`/platforms/${platform.slug}/`}
                    className="underline flex-1 px-6 py-5 text-sm font-medium text-center hover:text-gray-800"
                  >
                    <span>On</span> <span>{platform.name}</span>
                  </Link>
                ) : community.platform && community.platform.includes(",") ? (
                  <div className="flex-1 px-6 py-5 text-sm font-medium text-center flex space-x-2 items-middle">
                    <span>Platforms:</span>
                    {community.platform.split(",").map(p => (
                      <Link
                        to={`/platforms/${p}/`}
                        className="underline hover:text-gray-800"
                      >
                        <span>{capitalizeFirst(p)}</span>
                      </Link>
                    ))}
                  </div>
                ) : (
                  ""
                )}
                {community.established ? (
                  <div className="flex-1 flex px-6 py-5 text-sm font-medium text-center items-center justify-center space-x-1">
                    <FlagIcon className="h-4 w-4 opacity-50" />
                    <span>Established</span>{" "}
                    <span>{community.established}</span>
                  </div>
                ) : (
                  ""
                )}
                {community.members ? (
                  <div className="flex-1 flex px-6 py-5 text-sm font-medium text-center items-center justify-center space-x-1">
                    <UserGroupIcon className="h-4 w-4 opacity-50" />
                    <span>{readableNumber(displayMembers)}</span>{" "}
                    <span>Members</span>
                  </div>
                ) : (
                  ""
                )}
              </div>
            </div>
          </section>

          {/* details panel (overflow visible so topic rank badges can extend past the card) */}
          <div className="rounded-lg bg-white overflow-visible shadow p-6">
            <div className="mb-8">
              <h2
                className="text-base font-medium text-gray-900 mb-2"
                id="features-title"
              >
                Community Topics
              </h2>
              <TopicList
                topics={topics}
                mini={true}
                cols={2}
                topicRanksBySlug={topicRanksBySlug}
              />
            </div>

            <div className="mb-4">
              <h2
                className="text-base font-medium text-gray-900 mb-2"
                id="features-title"
              >
                Community Features
              </h2>
              <CommunityFeatures
                community={community}
                rareFeatures={pageContext.rareFeatures}
              />
            </div>
          </div>

          <GummySearchAnalysis
            gummysearch={gummysearch}
            communityName={community.name}
          />

          <div className="rounded-lg bg-white overflow-hidden shadow p-6">
            <CommunityReviews
              community={communityForRating}
              reviews={reviews}
            />
          </div>

          <CommunityFaq items={faqItems} />

          <div className="mt-0 text-xs flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="flex flex-wrap items-center gap-x-4 flex-1 min-w-0">
              {directOutboundUrl && !redirectDisabled ? (
                <a
                  href={directOutboundUrl}
                  target="_blank"
                  rel="noopener"
                  onClick={clickedDirectOutboundLink}
                  className="text-gray-500 opacity-75 hover:opacity-100 hover:underline flex items-center"
                >
                  <ExternalLinkIcon className="h-4 w-4 mr-1" aria-hidden />
                  <span className="font-semibold">Go to community</span>
                </a>
              ) : null}
              <Link
                to="/suggest/"
                className="text-gray-500 opacity-75 hover:opacity-100 hover:underline flex items-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                <span className="font-semibold">Suggest an edit</span>
              </Link>
              <a
                href={`mailto:contact@thehiveindex.com?subject=Report Community: ${community.name}`}
                className="text-gray-500 opacity-75 hover:opacity-100 hover:underline flex items-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"
                  />
                </svg>
                <span className="font-semibold">Report community</span>
              </a>
              {!community.claimed ? (
                <a
                  href="mailto:contact@thehiveindex.com?subject=Claim Community"
                  className="text-gray-500 opacity-75 hover:opacity-100 hover:underline flex items-center"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="h-4 w-4 mr-1"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M10.05 4.575a1.575 1.575 0 10-3.15 0v3m3.15-3v-1.5a1.575 1.575 0 013.15 0v1.5m-3.15 0l.075 5.925m3.075.75V4.575m0 0a1.575 1.575 0 013.15 0V15M6.9 7.575a1.575 1.575 0 10-3.15 0v8.175a6.75 6.75 0 006.75 6.75h2.018a5.25 5.25 0 003.712-1.538l1.732-1.732a5.25 5.25 0 001.538-3.712l.003-2.024a.668.668 0 01.198-.471 1.575 1.575 0 10-2.228-2.228 3.818 3.818 0 00-1.12 2.687M6.9 7.575V12m6.27 4.318A4.49 4.49 0 0116.35 15m.002 0h-.002"
                    />
                  </svg>

                  <span className="font-semibold">Claim community</span>
                </a>
              ) : null}
            </div>
            {listingDatesSummary ? (
              <p className="text-gray-400 text-right shrink-0 max-w-full mb-0 ml-auto">
                {listingDatesSummary}
              </p>
            ) : null}
          </div>
        </div>

        {/* Right column */}
        <div className="grid grid-cols-1 gap-4">
          <CommunityMonthlyStats community={community} />

          {similarCommunities && similarCommunities.length ? (
            <section aria-labelledby="recent-hires-title">
              <div className="rounded-lg bg-white overflow-hidden shadow p-6">
                <div className="flex items-center">
                  <h2
                    id="recent-hires-title"
                    className="shrink-0 text-base font-medium text-gray-900"
                  >
                    Similar Communities
                  </h2>
                  <span className="ml-2 shrink-0 font-semibold text-xs text-gray-500">
                    <span>{similarCommunities.length}</span>
                  </span>
                  {similarCommunities.length >
                    similarCommunitiesInitialVisible &&
                  !showAllSimilarCommunities ? (
                    <button
                      type="button"
                      aria-label={`Show ${similarCommunities.length -
                        similarCommunitiesInitialVisible} more similar communities`}
                      className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-full border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-2"
                      onClick={() => setShowAllSimilarCommunities(true)}
                    >
                      <span aria-hidden>+</span>
                      <span>
                        {similarCommunities.length -
                          similarCommunitiesInitialVisible}{" "}
                        more
                      </span>
                    </button>
                  ) : null}
                </div>
                <div className="mt-1">
                  <SimilarCommunities
                    communities={similarCommunities}
                    // hideTopPicks={community.promoted}
                    hideTopPicks={hideAds}
                    hideRatings={hideAds}
                    defaultCount={similarCommunitiesInitialVisible}
                    showAll={showAllSimilarCommunities}
                    minimalListView
                  />
                </div>
              </div>
            </section>
          ) : (
            ""
          )}

          <StickyScrollWrapper
            className="mt-0 min-w-0 self-start"
            contentClassName="min-w-0 space-y-4"
            boundarySelector="[data-community-page-grid]"
          >
            <PaidAds
              topics={topics}
              ads={data.paidAds.nodes}
              className="mt-0"
              location="Community"
            />

            {community.url ? (
              redirectDisabled ? (
                <span
                  data-for="community-redirect-disabled"
                  data-tip={REDIRECT_DISABLED_TOOLTIP}
                  className="block w-full cursor-not-allowed"
                >
                  <div
                    aria-disabled="true"
                    className="flex w-full items-center justify-between gap-3 px-4 py-2 border border-gray-300 text-gray-500 bg-gray-200 shadow-sm text-sm rounded-sm pointer-events-none"
                  >
                    <div className="min-w-0 flex-1 text-left">
                      <div className="font-medium truncate">{linkDisplay}</div>
                      <div className="opacity-75 text-xs font-semibold">
                        Go to community
                      </div>
                    </div>
                    <div className="flex-shrink-0">→</div>
                  </div>
                </span>
              ) : (
                <Link
                  to={internalLinkUrl}
                  state={redirectLinkState}
                  onClick={clickedLink}
                  rel="nofollow"
                  className="flex w-full items-center justify-between gap-3 px-4 py-2 border border-rose-600 text-white bg-rose-500 hover:bg-rose-600 shadow-sm text-sm rounded-sm"
                >
                  <div className="min-w-0 flex-1 text-left">
                    <div className="opacity-100 font-medium truncate">
                      {linkDisplay}
                    </div>
                    <div className="opacity-75 text-xs font-semibold">
                      Go to community
                    </div>
                  </div>
                  <div className="flex-shrink-0">→</div>
                </Link>
              )
            ) : null}
          </StickyScrollWrapper>

          {!hideAds && uniqueDeals && uniqueDeals.length ? (
            <Deals community={community} deals={uniqueDeals} />
          ) : (
            ""
          )}
        </div>
      </div>
    </React.Fragment>
  )

  return (
    <ModalRoutingContext.Consumer>
      {({ modal, closeTo }) => (
        <React.Fragment>
          {modal ? (
            <React.Fragment>
              <div className="flex flex-wrap items-center mb-4 pb-2 border-b">
                {closeTo ? (
                  <Link
                    to={closeTo}
                    className="text-gray-600 hover:text-gray-800 cursor-pointer flex items-center"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16l-4-4m0 0l4-4m-4 4h18"
                      />
                    </svg>
                    <div className="ml-2">Close</div>
                  </Link>
                ) : (
                  ""
                )}
                <div className="ml-4 sm:ml-8 mr-auto min-w-0">
                  <h1 className="text-lg sm:text-3xl font-bold text-gray-800 flex min-w-0 items-center gap-1.5">
                    <span className="min-w-0 truncate">{community.name}</span>
                    {inactivePlatformTitleAddon}
                    {community.claimed ? (
                      <CommunityVerifiedBadge className="h-6 w-6 flex-shrink-0 text-sky-500 sm:h-8 sm:w-8" />
                    ) : null}
                  </h1>
                </div>
                <div className="hidden sm:inline-block">
                  <ShareButtons
                    title={community.name}
                    buttonBackground="bg-gray-400"
                  />
                </div>
              </div>
              <div className="">{communityContents}</div>
            </React.Fragment>
          ) : (
            <Layout
              pageTitle={community.name}
              pageTitleAddon={inactivePlatformTitleAddon}
              pageTitleVerified={community.claimed}
              hidePageTitle={true}
              noBackground={true}
              enableShare={true}
            >
              {communityContents}
            </Layout>
          )}
        </React.Fragment>
      )}
    </ModalRoutingContext.Consumer>
  )
}

export const communityPageQuery = graphql`
  query CommunityPage(
    $slug: String
    $topicSlugs: [String]
    $platformSlugs: [String]!
    $similarCommunitySlugs: [String]
    $topicRankingCommunitySlugs: [String]
    $gummysearchSubredditName: String!
    $skipGummysearch: Boolean!
  ) {
    site {
      siteMetadata {
        title
      }
    }
    community: googleSpreadsheetSourceCommunities(slug: { eq: $slug }) {
      name
      slug
      members
      established
      description
      platform
      topics
      logo
      platformLogo
      promoted
      sponsored
      url
      forum
      chat
      apply
      pairing
      events
      perks
      jobs
      newsletter
      paid
      courses
      tools
      token
      gold
      affiliate
      reviewCount
      reviewAvg
      claimed
      visitors
      views
      redirects
      visitorsPrev
      viewsPrev
      redirectsPrev
      added
      updated
    }
    gummysearchSubreddit: gummysearch @skip(if: $skipGummysearch) {
      reddit_subreddit(where: { name: { _eq: $gummysearchSubredditName } }) {
        name
        data
        calculated
        results_updated_at
        results_analysis
        results_submissions_top
        products
      }
    }
    similarCommunities: allGoogleSpreadsheetSourceCommunities(
      filter: { slug: { in: $similarCommunitySlugs } }
    ) {
      nodes {
        name
        slug
        members
        established
        description
        platform
        logo
        platformLogo
        url
        promoted
        sponsored
        gold
        forum
        chat
        apply
        pairing
        events
        perks
        jobs
        newsletter
        paid
        reviewCount
        reviewAvg
      }
    }
    communityPlatforms: allGoogleSpreadsheetSourcePlatforms(
      filter: { slug: { in: $platformSlugs } }
    ) {
      nodes {
        name
        slug
        removed
        logoBig
        description
        groupName
        groupNamePlural
      }
    }
    topics: allGoogleSpreadsheetSourceTopics(
      filter: { slug: { in: $topicSlugs } }
    ) {
      nodes {
        slug
        name
        description
        count
        image
        people
        views
        viewsWithCommunities
        visitors
        redirects
      }
    }
    topicRankingCommunities: allGoogleSpreadsheetSourceCommunities(
      filter: {
        slug: { in: $topicRankingCommunitySlugs }
        removed: { ne: true }
      }
      sort: { fields: order, order: ASC }
    ) {
      nodes {
        slug
        topics
        promoted
        promotedTopics
        countFeatures
        redirects
        reviewCount
        reviewAvg
        order
        members
      }
    }
    reviews: allGoogleSpreadsheetSourceReviews(
      filter: { community: { eq: $slug }, approved: { eq: true } }
      sort: { fields: timestamp, order: DESC }
    ) {
      nodes {
        approved
        pinned
        community
        yourFirstName
        overall
        publicReview
        timestamp
        yourRelationshipToTheCommunity
      }
    }
    deals: allGoogleSpreadsheetSourceDeals(filter: { active: { eq: "TRUE" } }) {
      nodes {
        order
        title
        subtitle
        topic
        offer
        logo
        url
        order
      }
    }
    paidAds: allGoogleSpreadsheetSourceAds(
      filter: {
        topic: { in: $topicSlugs }
        approved: { eq: "1" }
        live: { eq: "TRUE" }
      }
      sort: { fields: order, order: DESC }
    ) {
      nodes {
        id
        slug
        topic
        productName
        shortProductDescription
        order
        live
        ctaText
        approved
        adType
        urlForYourAd
        logoIcon
        displayAd
      }
    }
  }
`

export default CommunityPage
